const DISMISSED_UNTIL = 'tf-newsletter-popup-dismissed-until';
const AWAITING_RESULT = 'tf-newsletter-popup-awaiting-result';

// Storage throws outright in some privacy modes, so every access is guarded and the popup
// simply behaves as if it had never been shown before.
const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const write = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch {
    /* nothing to do — the popup will show again next visit */
  }
};

const forget = (store, key) => {
  try {
    store.removeItem(key);
  } catch {
    /* as above */
  }
};

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.querySelector('.newsletter-popup__close')?.addEventListener('click', () => this.close());
    document.addEventListener('submit', this.#handleSubmit, true);

    // Shopify reloads the page after the customer form posts. If the post came from this
    // popup, reopen straight away so the visitor sees the outcome the server rendered.
    if (read(sessionStorage, AWAITING_RESULT)) {
      forget(sessionStorage, AWAITING_RESULT);
      this.open();
      return;
    }

    if (Number(read(localStorage, DISMISSED_UNTIL)) > Date.now()) return;

    this.timer = setTimeout(() => this.#watchSignupForms(), (Number(this.dataset.delay) || 0) * 1000);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.hideTimer);
    this.observer?.disconnect();
    document.removeEventListener('submit', this.#handleSubmit, true);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  // Offering the same thing twice on one screen is just noise, so the card and the footer
  // signup take turns: the card steps aside whenever a signup form is in view and comes
  // back once it scrolls away. The observer stays connected for the life of the page —
  // gating only the first appearance left the card sitting over the footer form.
  #watchSignupForms() {
    const forms = document.querySelectorAll('.email-signup-block');
    if (!forms.length || !('IntersectionObserver' in window)) {
      this.open();
      return;
    }

    const onScreen = new Set();
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }
      if (onScreen.size > 0) this.retract();
      else this.open();
    });
    // The first callback reports current state, so a visitor nowhere near the footer
    // still gets the card immediately.
    forms.forEach((form) => this.observer.observe(form));
  }

  // Any newsletter signup on the page counts, so subscribing through the footer form
  // silences the popup as well.
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector('input[name="contact[email]"]')) return;

    if (this.contains(form)) write(sessionStorage, AWAITING_RESULT, '1');
    this.#stayClosed();
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  open() {
    if (this.classList.contains('is-open')) return;
    clearTimeout(this.hideTimer);
    this.hidden = false;
    document.addEventListener('keydown', this.#handleKeydown);
    requestAnimationFrame(() => this.classList.add('is-open'));
  }

  // Out of the way, but not dismissed: it returns when the signup form leaves the screen.
  retract() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.#handleKeydown);
    // Hidden rather than left at opacity 0, which would still swallow clicks in the corner.
    this.hideTimer = setTimeout(() => {
      this.hidden = true;
    }, 300);
  }

  close() {
    this.#stayClosed();
    this.observer?.disconnect();
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.#handleKeydown);
    // Removed rather than left at opacity 0, which would still swallow clicks in the corner.
    setTimeout(() => this.remove(), 300);
  }

  #stayClosed() {
    const days = Number(this.dataset.days) || 30;
    write(localStorage, DISMISSED_UNTIL, String(Date.now() + days * 86400000));
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
